import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useTheme } from './ThemeContext';

/* ── Colour helpers for themed toasts ─────────────────────────── */
const kindColors = (theme: { success?: string; danger?: string; warning?: string; primary?: string }) => ({
  success: theme.success  || '#16A34A',
  error:   theme.danger   || '#DC2626',
  warning: theme.warning  || '#D97706',
  info:    theme.primary  || 'var(--action)',
});

type ToastKind = 'success' | 'info' | 'warning' | 'error';

export interface ShowToastOptions {
  message: string;
  kind?: ToastKind;
  icon?: string;
  durationMs?: number;
  idKey?: string; // used for deduplication window
  allowedPaths?: string[]; // show only when current path starts with one of these
}

interface ToastContextType {
  showToast: (options: ShowToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { theme } = useTheme();

  // Keep a short-term cache of recently shown ids to prevent duplicates
  const recentIdsRef = useRef<Map<string, number>>(new Map());
  const DEDUP_WINDOW_MS = 5000;

  const purgeOldEntries = useCallback(() => {
    const now = Date.now();
    for (const [key, ts] of recentIdsRef.current.entries()) {
      if (now - ts > DEDUP_WINDOW_MS) recentIdsRef.current.delete(key);
    }
  }, []);

  const showToast = useCallback((options: ShowToastOptions) => {
    const { message, kind = 'info', icon, durationMs, idKey, allowedPaths } = options;

    // Route filtering
    if (Array.isArray(allowedPaths) && allowedPaths.length > 0) {
      const matches = allowedPaths.some(prefix => pathname.startsWith(prefix));
      if (!matches) {
        if (import.meta.env.MODE !== 'production') {
          // Helpful debug to verify why a toast did not render
          console.debug('[ToastProvider] Skipping toast due to path filter', {
            pathname,
            allowedPaths
          });
        }
        return;
      }
    }

    // Deduplication
    purgeOldEntries();
    const key = idKey || `${kind}:${message}`;
    const lastTs = recentIdsRef.current.get(key);
    if (lastTs && Date.now() - lastTs < DEDUP_WINDOW_MS) {
      if (import.meta.env.MODE !== 'production') {
        console.debug('[ToastProvider] Skipping duplicate toast within window', { key });
      }
      return;
    }
    recentIdsRef.current.set(key, Date.now());

    const colors = kindColors(theme);
    const accentColor = colors[kind] || colors.info;

    const baseStyle: React.CSSProperties = {
      background: theme.components?.card?.background || '#FFFFFF',
      color: theme.text || '#2D2D2D',
      padding: '12px 14px',
      borderRadius: 8,
      fontSize: 14,
      borderLeft: `4px solid ${accentColor}`,
      boxShadow: theme.components?.card?.shadow || '0 1px 3px rgba(0,0,0,0.08)',
    };

    const durations: Record<ToastKind, number> = {
      success: 5000,
      info: 4000,
      warning: 5000,
      error: 6000,
    };

    const doToast = (kind: ToastKind) => {
      const opts = { duration: durationMs ?? durations[kind], icon, style: baseStyle } as const;
      switch (kind) {
        case 'success':
          toast.success(message, opts);
          break;
        case 'warning':
          toast(message, { ...opts, icon: icon ?? '⚠️' });
          break;
        case 'error':
          toast.error(message, opts);
          break;
        default:
          toast(message, opts);
      }
    };

    doToast(kind);
  }, [pathname, theme, purgeOldEntries]);

  const value = useMemo(() => ({ showToast }), [showToast]);

  // Global toast options so that direct toast.success/error calls (e.g. from
  // SocketContext) also pick up the current theme colours.
  const globalToastStyle: React.CSSProperties = {
    background: theme.components?.card?.background || '#FFFFFF',
    color: theme.text || '#2D2D2D',
    borderRadius: 8,
    fontSize: 14,
    boxShadow: theme.components?.card?.shadow || '0 1px 3px rgba(0,0,0,0.08)',
  };

  const colors = kindColors(theme);

  return (
    <ToastContext.Provider value={value}>
      {/* Single global Toaster rendered early in the tree */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        // Push toasts below iOS safe-area (notch / dynamic island) and add side
        // padding so landscape notch doesn't clip them either.
        containerStyle={{
          top: 'max(16px, calc(env(safe-area-inset-top) + 8px))',
          left: 'env(safe-area-inset-left)',
          right: 'env(safe-area-inset-right)',
        }}
        toastOptions={{
          style: globalToastStyle,
          success: {
            style: {
              ...globalToastStyle,
              borderLeft: `4px solid ${colors.success}`,
            },
          },
          error: {
            style: {
              ...globalToastStyle,
              borderLeft: `4px solid ${colors.error}`,
            },
          },
        }}
      />
      {children}
    </ToastContext.Provider>
  );
};


